import { mergeAttributes, Node } from '@tiptap/core';
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { TTS_CARET_ANCHOR, TTS_LIMITS } from '../constant';

interface PauseTagOptions {
  HTMLAttributes: Record<string, unknown>;
}

function getPauseTagLabel(duration: number): string {
  return `<#${duration}#>`;
}

function isPauseTagInsideEmotionTag(props: Pick<NodeViewProps, 'editor' | 'getPos'>): boolean {
  if (typeof props.getPos !== 'function') {
    return false;
  }

  const pos = props.getPos();
  if (typeof pos !== 'number') {
    return false;
  }

  const $pos = props.editor.state.doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if ($pos.node(depth).type.name === 'emotionTag') {
      return true;
    }
  }

  return false;
}

function getPauseTagTextClassName(isInsideEmotionTag: boolean): string {
  return isInsideEmotionTag
    ? 'inline-flex h-[23px] cursor-pointer items-center justify-center rounded-8 border px-[9px] text-center font-semibold text-[12px] leading-[17px] transition-colors'
    : 'inline-flex h-[30px] cursor-pointer items-center justify-center rounded-8 border px-[9px] text-center font-semibold text-sm leading-[20px] transition-colors';
}

function PauseTagNodeView({ node, editor, getPos }: NodeViewProps) {
  const duration = node.attrs.duration as number;
  const disabled = node.attrs.disabled as boolean;
  const isOutOfRange = duration < TTS_LIMITS.MIN_PAUSE || duration > TTS_LIMITS.MAX_PAUSE;
  const isDisabled = disabled || isOutOfRange;
  const isInsideEmotionTag = isPauseTagInsideEmotionTag({ editor, getPos });

  return (
    <NodeViewWrapper
      as="span"
      data-pause-tag=""
      data-duration={duration}
      data-disabled={isDisabled ? 'true' : 'false'}
      data-inside-emotion-tag={isInsideEmotionTag ? 'true' : 'false'}
      className="inline-flex select-none whitespace-nowrap px-0.5 align-middle"
    >
      <span
        className={getPauseTagTextClassName(isInsideEmotionTag)}
        style={{
          background: isDisabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.16)',
          borderColor: isDisabled ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
          color: isDisabled ? 'rgba(255,255,255,0.32)' : '#fff',
          opacity: isDisabled ? '0.5' : '1',
        }}
      >
        {getPauseTagLabel(duration)}
      </span>
    </NodeViewWrapper>
  );
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    pauseTag: {
      insertPauseTag: (duration?: number) => ReturnType;
    };
  }
}

export const PauseTag = Node.create<PauseTagOptions>({
  name: 'pauseTag',
  group: 'inline',
  inline: true,
  selectable: true,
  draggable: false,
  atom: true,
  marks: '',
  content: '',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      duration: { default: 0.5 },
      disabled: { default: false },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-pause-tag]',
      },
    ];
  },

  renderHTML({ node }) {
    const duration = node.attrs.duration as number;
    const disabled = node.attrs.disabled as boolean;
    const isOutOfRange = duration < TTS_LIMITS.MIN_PAUSE || duration > TTS_LIMITS.MAX_PAUSE;
    const isDisabled = disabled || isOutOfRange;

    return [
      'span',
      mergeAttributes({
        ...this.options.HTMLAttributes,
        'data-pause-tag': '',
        'data-duration': duration,
        'data-disabled': isDisabled ? 'true' : 'false',
        contenteditable: false,
        class: 'inline-flex px-0.5 align-middle select-none whitespace-nowrap',
      }),
      [
        'span',
        {
          class: getPauseTagTextClassName(false),
          style: `background: ${isDisabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.16)'}; border-color: ${isDisabled ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)'}; color: ${isDisabled ? 'rgba(255,255,255,0.32)' : '#fff'}; opacity: ${isDisabled ? '0.5' : '1'}`,
        },
        getPauseTagLabel(duration),
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PauseTagNodeView);
  },

  addCommands() {
    return {
      insertPauseTag:
        (duration = 0.5) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: { duration, disabled: false },
            })
            .insertContent(TTS_CARET_ANCHOR)
            .run();
        },
    };
  },
});
